/**
 * collection-page.js
 * Rutambhara Theme — Collection Page Logic
 *
 * Handles: Load More, Sort, Filter sidebar, Accordion toggles
 * Liquid values passed via data attributes on the section element:
 *   data-use-hybrid-filters, data-total-products
 */

document.addEventListener('DOMContentLoaded', function () {
  const section = document.querySelector('.main-collection[data-section-id]');
  if (!section) return;

  const useHybridFilters = section.dataset.useHybridFilters === 'true';
  let totalProducts = parseInt(section.dataset.totalProducts) || 0;

  // ========== LOAD MORE ==========
  const productsGrid = section.querySelector('[data-products-grid]');

  // Delegated from the section rather than bound to the button element itself.
  // AJAX filtering (collection-filters-v2.js) rewrites the Load More container,
  // which destroys the original button -- a listener bound directly to it would
  // go with it, leaving a button that looks live but does nothing.
  if (productsGrid) {
    section.addEventListener('click', async function (event) {
      const btn = event.target.closest('[data-load-more-btn]');
      if (!btn) return;

      const nextUrl = btn.getAttribute('data-next-url');
      if (!nextUrl) return;

      // Re-queried per click: both the container and the running total change
      // whenever a filter is applied, so neither can be captured once at load.
      const loadMoreContainer = section.querySelector('[data-load-more-container]');
      const loadMoreStatus = section.querySelector('[data-load-more-status]');
      let totalLoaded = productsGrid.querySelectorAll('[data-product-item]').length;

      const textEl = btn.querySelector('.collection-load-more__text');
      const spinnerEl = btn.querySelector('.collection-load-more__spinner');

      if (textEl) textEl.style.display = 'none';
      if (spinnerEl) spinnerEl.style.display = 'inline-flex';
      btn.disabled = true;

      try {
        const sectionId = productsGrid.getAttribute('data-section-id');
        const separator = nextUrl.includes('?') ? '&' : '?';
        const fetchUrl = nextUrl + separator + 'section_id=' + sectionId;

        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error('Failed to load');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newGrid = doc.querySelector('[data-products-grid]');
        if (newGrid) {
          const newItems = Array.from(newGrid.querySelectorAll('[data-product-item]'));

          // Pass 1: set initial hidden state and append all items (single reflow)
          newItems.forEach(function (item) {
            item.style.opacity = '0';
            item.style.transform = 'translateY(20px)';
            productsGrid.appendChild(item);
          });

          // Pass 2: animate all items in one requestAnimationFrame (no per-item reflow)
          requestAnimationFrame(function () {
            newItems.forEach(function (item) {
              item.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
              item.style.opacity = '1';
              item.style.transform = 'translateY(0)';
            });
          });

          totalLoaded += newItems.length;
        }

        const newLoadMoreBtn = doc.querySelector('[data-load-more-btn]');
        if (newLoadMoreBtn && newLoadMoreBtn.getAttribute('data-next-url')) {
          btn.setAttribute('data-next-url', newLoadMoreBtn.getAttribute('data-next-url'));
          if (textEl) textEl.style.display = 'inline';
          if (spinnerEl) spinnerEl.style.display = 'none';
          btn.disabled = false;
        } else if (loadMoreContainer) {
          loadMoreContainer.innerHTML = '<p class="collection-load-more__status">All products loaded</p>';
        }

        if (loadMoreStatus && loadMoreContainer && loadMoreContainer.querySelector('[data-load-more-status]')) {
          loadMoreStatus.textContent = 'Showing ' + totalLoaded + ' of ' + totalProducts + ' products';
        }

        if (typeof window.initProductCards === 'function') {
          window.initProductCards();
        }
      } catch (error) {
        console.error('Load more error:', error);
        if (textEl) textEl.textContent = 'Try Again';
        if (textEl) textEl.style.display = 'inline';
        if (spinnerEl) spinnerEl.style.display = 'none';
        btn.disabled = false;
      }
    });
  }

  // ========== HYBRID FILTERS ==========
  // If hybrid filters are active, external JS handles everything
  // We only handle the sort select here
  if (useHybridFilters) {
    const sortSelect = section.querySelector('[data-sort-select]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.value);
        window.location.href = url.toString();
      });
    }
    return;
  }

  // ========== STANDARD FILTER SIDEBAR ==========
  const openFiltersBtn = section.querySelector('[data-open-filters]');
  const closeFiltersBtn = section.querySelector('[data-close-filters]');
  const sidebar = section.querySelector('[data-collection-sidebar]');
  const overlay = section.querySelector('[data-sidebar-overlay]');

  function openFilters() {
    if (sidebar) sidebar.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    if (overlay) overlay.classList.add('is-visible');
  }

  function closeFilters() {
    if (sidebar) sidebar.classList.remove('is-open');
    document.body.style.overflow = '';
    if (overlay) overlay.classList.remove('is-visible');
  }

  if (openFiltersBtn) openFiltersBtn.addEventListener('click', openFilters);
  if (closeFiltersBtn) closeFiltersBtn.addEventListener('click', closeFilters);
  if (overlay) overlay.addEventListener('click', closeFilters);

  // Filter accordion toggles
  section.querySelectorAll('[data-filter-toggle]').forEach(function (toggle) {
    toggle.addEventListener('click', function () {
      const expanded = this.getAttribute('aria-expanded') === 'true';
      this.setAttribute('aria-expanded', !expanded);
      const content = this.nextElementSibling;
      if (content) {
        content.style.maxHeight = expanded ? '0' : content.scrollHeight + 'px';
      }
    });
  });

  // Initialize accordion heights
  section.querySelectorAll('.collection-filter__content').forEach(function (content) {
    content.style.maxHeight = content.scrollHeight + 'px';
  });

  // Sort select
  const sortSelect = section.querySelector('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.value);
      window.location.href = url.toString();
    });
  }

  // Filter form submission
  const filterForm = section.querySelector('[data-collection-filters]');
  if (filterForm) {
    filterForm.addEventListener('submit', function (e) {
      e.preventDefault();
      const formData = new FormData(this);
      const url = new URL(window.location.origin + window.location.pathname);

      const currentSort = new URL(window.location.href).searchParams.get('sort_by');
      if (currentSort) url.searchParams.set('sort_by', currentSort);

      formData.forEach(function (value, key) {
        if (value) url.searchParams.append(key, value);
      });

      window.location.href = url.toString();
    });
  }

  // Escape key closes sidebar
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sidebar && sidebar.classList.contains('is-open')) {
      closeFilters();
    }
  });
});
